import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOptionalWorkspace } from '../lib/workspace';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import {
  getAlertPreferences,
  updateAlertPreferences,
  type AlertCadence,
  type AlertCategory,
} from '../lib/api';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  PageHeader,
  Radio,
  Spinner,
  alertCategoryLabel,
} from '@aigovops/design-system';
import { roster } from '../data/editors';

const CATEGORY_VALUES: AlertCategory[] = ['legislation', 'litigation', 'framework', 'breach', 'cert'];

const CADENCE_META: Record<AlertCadence, { label: string; hint: string }> = {
  immediate: { label: 'Immediate', hint: 'Best for on-call CISOs — every alert delivers as it lands.' },
  daily: { label: 'Daily digest', hint: 'Default for legal + operator roles — one roundup each morning.' },
  weekly: { label: 'Weekly digest', hint: 'Best for stakeholders reviewing at a rhythm, not a stream.' },
};

const CHANNEL_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'email', label: 'Email' },
  { id: 'slack', label: 'Slack' },
];

/**
 * UC-4 R2 H12 section order: Categories (hero) → Relevance filter →
 * Delivery channels → Cadence → Automation. In-app is always on (a
 * security posture decision — customers must always retain the audit
 * trail of what they were shown), rendered disabled per H8.
 *
 * Relevance filter + auto-snooze: both persist here, but neither is
 * enforced server-side yet (`listAlerts()` doesn't read
 * `relevance_targets_only`; no cron reads `auto_snooze`) — copy below
 * says "saved" rather than claiming a live filtering/automation effect
 * that doesn't exist (ux-adversary Marcus High finding).
 */
export function AlertPreferences() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const prefix = slug ? `/w/${slug}/app` : '/app'; // scope-check-ignore: legacy fallback

  const prefsQuery = useQuery({
    queryKey: queryKeys.alertPreferences(slug ?? ''),
    queryFn: () => getAlertPreferences(slug!),
    enabled: !!slug,
  });
  const prefsState = {
    data: prefsQuery.data ?? null,
    error: prefsQuery.error?.message ?? null,
    loading: prefsQuery.isLoading,
    refetch: prefsQuery.refetch,
  };

  const [categories, setCategories] = useState<Set<AlertCategory>>(new Set());
  const [cadence, setCadence] = useState<AlertCadence>('daily');
  const [channels, setChannels] = useState<Set<string>>(new Set());
  const [relevanceOnly, setRelevanceOnly] = useState(true);
  const [autoSnooze, setAutoSnooze] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const p = prefsState.data?.preferences;
    if (!p) return;
    setCategories(new Set(p.categories));
    setCadence(p.cadence);
    setChannels(new Set(p.channels.filter((c) => c !== 'in_app')));
    setRelevanceOnly(p.relevance_targets_only);
    setAutoSnooze(p.auto_snooze);
  }, [prefsState.data]);

  function toggleCategory(c: AlertCategory) {
    setSaved(false);
    setCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function toggleChannel(id: string) {
    setSaved(false);
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    if (!slug) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await updateAlertPreferences(slug, {
        categories: [...categories],
        cadence,
        channels: [...channels, 'in_app'],
        relevance_targets_only: relevanceOnly,
        auto_snooze: autoSnooze,
      });
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  }

  if (!workspaceCtx || prefsState.loading) return <Spinner label="Loading preferences" />;
  if (prefsState.error) return <Alert variant="error">{prefsState.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '40rem' }}>
      <Link
        to={`${prefix}/alerts`}
        style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--secondary)', textDecoration: 'none' }}
      >
        ← Back to alerts
      </Link>

      <PageHeader
        title="Alert preferences"
        subtitle="What you're subscribed to, how often, and where it's delivered."
        freshness={{
          updatedAt: prefsState.data ? new Date(prefsState.data.preferences.updated_at).toISOString() : new Date().toISOString(),
          source: 'Your saved alert subscription',
          reviewer: roster,
        }}
        right={
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        }
      />

      {saveError ? <Alert variant="error" onDismiss={() => setSaveError(null)}>{saveError}</Alert> : null}
      {saved ? <Alert variant="success" onDismiss={() => setSaved(false)}>Preferences saved.</Alert> : null}

      <Card title="Categories">
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
          Uncheck a category to stop receiving that kind of alert. Your role's defaults are pre-checked.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {CATEGORY_VALUES.map((c) => (
            <Checkbox
              key={c}
              id={`cat-${c}`}
              label={alertCategoryLabel(c)}
              checked={categories.has(c)}
              onChange={() => toggleCategory(c)}
            />
          ))}
        </div>
      </Card>

      <Card title="Relevance filter">
        <Checkbox
          id="relevance-only"
          label="Only alerts affecting my target certifications"
          description="Saved as your preference. Inbox filtering by accreditation target is coming soon — until then this doesn't change what you see today."
          checked={relevanceOnly}
          onChange={(e) => {
            setSaved(false);
            setRelevanceOnly(e.target.checked);
          }}
        />
      </Card>

      <Card title="Delivery channels">
        <Checkbox id="chan-in-app" label="In-app inbox" description="Always on — this is your audit trail of what you were shown." checked disabled />
        {CHANNEL_OPTIONS.map((ch) => (
          <Checkbox
            key={ch.id}
            id={`chan-${ch.id}`}
            label={ch.label}
            checked={channels.has(ch.id)}
            onChange={() => toggleChannel(ch.id)}
          />
        ))}
      </Card>

      <Card title="Cadence">
        <div role="radiogroup" aria-label="Delivery cadence" style={{ display: 'grid', gap: '2px' }}>
          {(Object.keys(CADENCE_META) as AlertCadence[]).map((k) => (
            <Radio
              key={k}
              name="cadence"
              id={`cadence-${k}`}
              label={CADENCE_META[k].label}
              description={CADENCE_META[k].hint}
              checked={cadence === k}
              onChange={() => {
                setSaved(false);
                setCadence(k);
              }}
            />
          ))}
        </div>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
          Critical alerts always deliver immediately regardless of cadence.
        </p>
      </Card>

      <Card title="Automation">
        <Checkbox
          id="auto-snooze"
          label="Auto-snooze alerts once I've read them"
          description="Saved as your preference. The automation that acts on it ships in a later PR."
          checked={autoSnooze}
          onChange={(e) => {
            setSaved(false);
            setAutoSnooze(e.target.checked);
          }}
        />
      </Card>
    </div>
  );
}
